import {
  CalendarDetailsVisibility,
  ID,
  LinkedItemType,
  SyncMode,
} from '@griply/types';
import {
  useCalendarSyncs,
  useExternalCalendarConnections,
  useRemoveCalendarSync,
  useSetCalendarSync,
  useTwoWaySyncAllowed,
} from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { useExternalConnectionsCalendarsQueries } from 'shared/hooks/queries/use-external-connections-calendars-queries';

import { CalendarSelectValue } from './calendar-select';
import { CalendarSyncOptionRow } from './calendar-sync-option-row';
import * as Styled from './calendar-sync-options.style';

type DraftRow = {
  key: string;
  linkedItemId: ID | null;
  linkedItemType: LinkedItemType | null;
  calendar: CalendarSelectValue | null;
  mode: SyncMode | null;
  detailsVisibility: CalendarDetailsVisibility | null;
};

const emptyDraft = (key: string): DraftRow => ({
  key,
  linkedItemId: null,
  linkedItemType: null,
  calendar: null,
  mode: SyncMode.Automatic,
  detailsVisibility: CalendarDetailsVisibility.Visible,
});

export const CalendarSyncOptionsList: React.FC = () => {
  const { t } = useTranslation();
  const connections = useExternalCalendarConnections();
  const syncs = useCalendarSyncs();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const setCalendarSync = useSetCalendarSync();
  const removeCalendarSync = useRemoveCalendarSync();
  const { allowed } = useTwoWaySyncAllowed();

  const connectionIds = useMemo(
    () => connections.map(({ id }) => id),
    [connections],
  );
  const { calendars } = useExternalConnectionsCalendarsQueries(connectionIds);

  const draftKey = useRef(0);
  const [drafts, setDrafts] = useState<DraftRow[]>([]);

  const updateDraft = (key: string, changes: Partial<DraftRow>) => {
    setDrafts((current) =>
      current.flatMap((draft) => {
        if (draft.key !== key) {
          return [draft];
        }
        const next = { ...draft, ...changes };
        if (
          next.linkedItemId &&
          next.linkedItemType &&
          next.calendar &&
          next.mode
        ) {
          setCalendarSync({
            connectionId: next.calendar.connectionId,
            linkedItemId: next.linkedItemId,
            linkedItemType: next.linkedItemType,
            calendarId: next.calendar.calendarId,
            mode: next.mode,
            detailsVisibility: next.detailsVisibility ?? undefined,
          });
          return [];
        }
        return [next];
      }),
    );
  };

  const removeDraft = (key: string) =>
    setDrafts((current) => current.filter((draft) => draft.key !== key));

  const onAddRow = () => {
    draftKey.current += 1;
    setDrafts((current) => [
      ...current,
      emptyDraft(`draft-${draftKey.current}`),
    ]);
  };

  if (!connections.length) {
    return null;
  }

  return (
    <Styled.Section>
      <Styled.Title>
        {t('forms.user-settings.sections.calendar.sync-tasks.title')}
      </Styled.Title>
      <Styled.Description>
        {t('forms.user-settings.sections.calendar.sync-tasks.description')}
      </Styled.Description>

      <Styled.Table>
        <Styled.HeaderRow>
          <Styled.ColumnLabel>
            {t(
              'forms.user-settings.sections.calendar.sync-tasks.columns.tasks',
            )}
          </Styled.ColumnLabel>
          <span />
          <Styled.ColumnLabel>
            {t(
              'forms.user-settings.sections.calendar.sync-tasks.columns.calendar',
            )}
          </Styled.ColumnLabel>
          <Styled.ColumnLabel>
            {t(
              'forms.user-settings.sections.calendar.sync-tasks.columns.task-sync',
            )}
          </Styled.ColumnLabel>
          <Styled.ColumnLabel>
            {t(
              'forms.user-settings.sections.calendar.sync-tasks.columns.task-details',
            )}
          </Styled.ColumnLabel>
          <span />
        </Styled.HeaderRow>

        {syncs.map((sync) => {
          const onChangeLinkedItem = (
            link: { id: ID; type: LinkedItemType } | null,
          ) => {
            removeCalendarSync({
              connectionId: sync.connectionId,
              linkedItemId: sync.linkedItemId,
            });
            if (link) {
              setCalendarSync({
                connectionId: sync.connectionId,
                linkedItemId: link.id,
                linkedItemType: link.type,
                calendarId: sync.calendarId,
                mode: sync.mode,
                detailsVisibility: sync.detailsVisibility,
              });
            }
          };
          const onChangeCalendar = (value: CalendarSelectValue) => {
            if (value.connectionId !== sync.connectionId) {
              removeCalendarSync({
                connectionId: sync.connectionId,
                linkedItemId: sync.linkedItemId,
              });
            }
            setCalendarSync({
              connectionId: value.connectionId,
              linkedItemId: sync.linkedItemId,
              linkedItemType: sync.linkedItemType,
              calendarId: value.calendarId,
              mode: sync.mode,
              detailsVisibility: sync.detailsVisibility,
            });
          };
          const onChangeMode = (mode: SyncMode) =>
            setCalendarSync({
              connectionId: sync.connectionId,
              linkedItemId: sync.linkedItemId,
              linkedItemType: sync.linkedItemType,
              calendarId: sync.calendarId,
              mode,
              detailsVisibility: sync.detailsVisibility,
            });
          const onChangeVisibility = (
            detailsVisibility: CalendarDetailsVisibility,
          ) =>
            setCalendarSync({
              connectionId: sync.connectionId,
              linkedItemId: sync.linkedItemId,
              linkedItemType: sync.linkedItemType,
              calendarId: sync.calendarId,
              mode: sync.mode,
              detailsVisibility,
            });
          const onRemove = () =>
            removeCalendarSync({
              connectionId: sync.connectionId,
              linkedItemId: sync.linkedItemId,
            });

          return (
            <CalendarSyncOptionRow
              key={`${sync.connectionId}:${sync.linkedItemId}`}
              linkedItemId={sync.linkedItemId}
              calendar={{
                connectionId: sync.connectionId,
                calendarId: sync.calendarId,
              }}
              mode={sync.mode}
              detailsVisibility={sync.detailsVisibility ?? null}
              goals={goals}
              lifeAreas={lifeAreas}
              calendars={calendars}
              onChangeLinkedItem={onChangeLinkedItem}
              onChangeCalendar={onChangeCalendar}
              onChangeMode={onChangeMode}
              onChangeVisibility={onChangeVisibility}
              onRemove={onRemove}
            />
          );
        })}

        {drafts.map((draft) => (
          <CalendarSyncOptionRow
            key={draft.key}
            linkedItemId={draft.linkedItemId}
            calendar={draft.calendar}
            mode={draft.mode}
            detailsVisibility={draft.detailsVisibility}
            goals={goals}
            lifeAreas={lifeAreas}
            calendars={calendars}
            onChangeLinkedItem={(link) =>
              updateDraft(draft.key, {
                linkedItemId: link?.id ?? null,
                linkedItemType: link?.type ?? null,
              })
            }
            onChangeCalendar={(calendar) =>
              updateDraft(draft.key, { calendar })
            }
            onChangeMode={(mode) => updateDraft(draft.key, { mode })}
            onChangeVisibility={(detailsVisibility) =>
              updateDraft(draft.key, { detailsVisibility })
            }
            onRemove={() => removeDraft(draft.key)}
          />
        ))}
      </Styled.Table>

      {allowed ? (
        <Styled.AddButton type="button" onClick={onAddRow}>
          <Styled.AddIconContainer>
            <Icon icon={Plus} />
          </Styled.AddIconContainer>
          {t('forms.user-settings.sections.calendar.sync-tasks.add')}
        </Styled.AddButton>
      ) : (
        <Styled.Upsell>
          {t('forms.user-settings.sections.calendar.sync-tasks.upsell')}
        </Styled.Upsell>
      )}
    </Styled.Section>
  );
};
